/**
 * The sheet picker's decisions (ai-plan-builder R27, R54; board T): whether the trainer must say how
 * the plan runs, and what still keeps „Zbuduj plan z N arkuszy" from going.
 *
 * A plan runs with dates on the weekdays its sheets name. When none of the CHOSEN sheets names one,
 * the trainer picks the scheduling mode in the picker, before anything is read (owner, 6 October
 * 2026). A listing from a server that does not say whether a sheet names weekdays asks nothing; the
 * server's own refusal (`ai_import_mode_required`) asks then.
 *
 * Pure: no React, no i18next.
 */

import type { AiImportSchedulingMode } from '~/entities/ai-conversation';

export interface PickerSheet {
  readonly name: string;
  readonly rows: number;
  readonly importable: boolean;
  /** Whether the sheet names weekdays, in its name or a cell; `null` when the listing does not say. */
  readonly namesWeekdays: boolean | null;
}

/** What keeps the import from going: no sheet chosen, or no scheduling mode where one is asked. */
export type PickerBlocker = 'chooseSheets' | 'chooseMode' | null;

export interface PickerState {
  /** The scheduling-mode question stands in the picker. */
  readonly asksMode: boolean;
  readonly blocker: PickerBlocker;
  /** The mode the import sends: the trainer's choice where it is asked, else none. */
  readonly sends: AiImportSchedulingMode | null;
}

export function pickerState(
  sheets: readonly PickerSheet[],
  chosen: ReadonlySet<string>,
  mode: AiImportSchedulingMode | null,
  /** The server refused this choice for want of a mode (`ai_import_mode_required`). */
  modeRequired: boolean,
): PickerState {
  const picked = sheets.filter((sheet) => chosen.has(sheet.name));
  const asksMode = picked.length > 0 && (modeRequired || picked.every((sheet) => sheet.namesWeekdays === false));
  const blocker: PickerBlocker = picked.length === 0 ? 'chooseSheets' : asksMode && mode === null ? 'chooseMode' : null;
  return { asksMode, blocker, sends: asksMode ? mode : null };
}
